import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import { TransferHistoryRow } from "./rfidHelpers";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { RFID_BY_UID_QUERY_EXPANDED } from "./queries";
import toast from "react-hot-toast";

const client = createApolloClient();

interface ReverseTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    transferId: number,
    sourceUid: string,
    destinationUid: string,
    tokensToReverse: any
  ) => Promise<void>;
  transfer: TransferHistoryRow | null;
}

interface TokenBalance {
  balance: number;
  redeemable_token_id: string;
  redeemable_token_name: string;
}

export default function ReverseTransferModal({
  isOpen,
  onClose,
  onConfirm,
  transfer,
}: ReverseTransferModalProps) {
  const initialFocusRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingBalances, setIsFetchingBalances] = useState(false);
  const [sourceCurrentBalance, setSourceCurrentBalance] = useState<any>(null);
  const [destCurrentBalance, setDestCurrentBalance] = useState<any>(null);
  const [sourceCurrentCash, setSourceCurrentCash] = useState<number>(0);
  const [destCurrentCash, setDestCurrentCash] = useState<number>(0);
  const [sourceCurrentPromo, setSourceCurrentPromo] = useState<number>(0);
  const [destCurrentPromo, setDestCurrentPromo] = useState<number>(0);
  const [reversalDetails, setReversalDetails] = useState<{
    tokensToRemove: any;
    tokensToAdd: any;
    cashBalanceToReverse: number;
    promoBalanceToReverse: number;
    warnings: string[];
    sourceAfterBalance: any;
    destAfterBalance: any;
    sourceAfterCashBalance: number;
    destAfterCashBalance: number;
    sourceAfterPromoBalance: number;
    destAfterPromoBalance: number;
  } | null>(null);

  useEffect(() => {
    if (isOpen && transfer) {
      fetchCurrentBalances();
    }
  }, [isOpen, transfer]);

  // Reset loading state when modal closes or opens
  useEffect(() => {
    if (!isOpen) {
      setIsLoading(false);
    }
  }, [isOpen]);

  const fetchCurrentBalances = async () => {
    if (!transfer) return;

    console.log("[REVERSAL DEBUG] Transfer data:", transfer);

    setIsFetchingBalances(true);
    try {
      const [sourceData, destData] = await Promise.all([
        client.query({
          query: RFID_BY_UID_QUERY_EXPANDED,
          variables: { uid: transfer.source_uid },
        }),
        client.query({
          query: RFID_BY_UID_QUERY_EXPANDED,
          variables: { uid: transfer.destination_uid },
        }),
      ]);

      const sourceAsset = sourceData.data.rfid_assets[0];
      const destAsset = destData.data.rfid_assets[0];

      setSourceCurrentBalance(sourceAsset?.tokens_balance || {});
      setDestCurrentBalance(destAsset?.tokens_balance || {});
      setSourceCurrentCash(sourceAsset?.cash_balance || 0);
      setDestCurrentCash(destAsset?.cash_balance || 0);
      setSourceCurrentPromo(sourceAsset?.promo_balance || 0);
      setDestCurrentPromo(destAsset?.promo_balance || 0);

      calculateReversalDetails(
        sourceAsset?.tokens_balance || {},
        destAsset?.tokens_balance || {},
        transfer.destination_payload,
        sourceAsset?.cash_balance || 0,
        destAsset?.cash_balance || 0,
        sourceAsset?.promo_balance || 0,
        destAsset?.promo_balance || 0,
        transfer.destination_cash_balance || 0,
        transfer.destination_promo_balance || 0
      );
    } catch (error) {
      console.error("Error fetching balances:", error);
      toast.error("Failed to fetch current balances");
      onClose();
    } finally {
      setIsFetchingBalances(false);
    }
  };

  const calculateReversalDetails = (
    sourceCurrent: any,
    destCurrent: any,
    transferredTokens: any,
    sourceCurrentCash: number,
    destCurrentCash: number,
    sourceCurrentPromo: number,
    destCurrentPromo: number,
    transferredCash: number,
    transferredPromo: number
  ) => {
    console.log("[REVERSAL DEBUG] Starting calculation with:", {
      sourceCurrent,
      destCurrent,
      transferredTokens,
      sourceCurrentCash,
      destCurrentCash,
      sourceCurrentPromo,
      destCurrentPromo,
      transferredCash,
      transferredPromo,
    });

    const warnings: string[] = [];
    const tokensToRemove: any = {};
    const tokensToAdd: any = {};
    let cashBalanceToReverse = 0;
    let promoBalanceToReverse = 0;

    // Extract tokens_balance if it's nested in the payload
    const tokens = transferredTokens?.tokens_balance || null;
    console.log("[REVERSAL DEBUG] Extracted tokens:", tokens);

    // Only process tokens if we have actual token data
    if (tokens && typeof tokens === "object") {
      // Calculate what can actually be reversed
      Object.entries(tokens).forEach(([tokenId, tokenData]: [string, any]) => {
        // Skip non-token properties like cash_balance, promo_balance
        if (!tokenData || typeof tokenData !== "object" || !tokenData.redeemable_token_name) {
          return;
        }

        const transferredAmount = tokenData.balance || 0;
        const destCurrentAmount =
          destCurrent[tokenId]?.balance || 0;

        if (destCurrentAmount >= transferredAmount) {
          // Full reversal possible
          tokensToRemove[tokenId] = {
            ...tokenData,
            balance: transferredAmount,
          };
          tokensToAdd[tokenId] = {
            ...tokenData,
            balance: transferredAmount,
          };
        } else {
          // Partial reversal - can only return what's available
          if (destCurrentAmount > 0) {
            tokensToRemove[tokenId] = {
              ...tokenData,
              balance: destCurrentAmount,
            };
            tokensToAdd[tokenId] = {
              ...tokenData,
              balance: destCurrentAmount,
            };
            warnings.push(
              `${tokenData.redeemable_token_name}: Only ${destCurrentAmount} of ${transferredAmount} tokens can be reversed`
            );
          } else {
            warnings.push(
              `${tokenData.redeemable_token_name}: No tokens available to reverse (0 of ${transferredAmount})`
            );
          }
        }
      });
    }

    // Handle cash balance reversal
    if (transferredCash > 0) {
      if (destCurrentCash >= transferredCash) {
        cashBalanceToReverse = transferredCash;
      } else {
        if (destCurrentCash > 0) {
          cashBalanceToReverse = destCurrentCash;
          warnings.push(
            `Cash Balance: Only $${(destCurrentCash / 100).toFixed(2)} of $${(transferredCash / 100).toFixed(2)} can be reversed`
          );
        } else {
          warnings.push(
            `Cash Balance: No balance available to reverse ($0 of $${(transferredCash / 100).toFixed(2)})`
          );
        }
      }
    }

    // Handle promo balance reversal
    if (transferredPromo > 0) {
      if (destCurrentPromo >= transferredPromo) {
        promoBalanceToReverse = transferredPromo;
      } else {
        if (destCurrentPromo > 0) {
          promoBalanceToReverse = destCurrentPromo;
          warnings.push(
            `Promo Balance: Only $${(destCurrentPromo / 100).toFixed(2)} of $${(transferredPromo / 100).toFixed(2)} can be reversed`
          );
        } else {
          warnings.push(
            `Promo Balance: No balance available to reverse ($0 of $${(transferredPromo / 100).toFixed(2)})`
          );
        }
      }
    }

    // Calculate after-reversal balances
    const sourceAfterBalance = { ...sourceCurrent };
    const destAfterBalance = { ...destCurrent };

    // Add tokens back to source
    Object.entries(tokensToAdd).forEach(([tokenId, tokenData]: [string, any]) => {
      if (sourceAfterBalance[tokenId]) {
        sourceAfterBalance[tokenId] = {
          ...sourceAfterBalance[tokenId],
          balance: (sourceAfterBalance[tokenId].balance || 0) + tokenData.balance,
        };
      } else {
        sourceAfterBalance[tokenId] = tokenData;
      }
    });

    // Remove tokens from destination
    Object.entries(tokensToRemove).forEach(([tokenId, tokenData]: [string, any]) => {
      if (destAfterBalance[tokenId]) {
        destAfterBalance[tokenId] = {
          ...destAfterBalance[tokenId],
          balance: Math.max(0, (destAfterBalance[tokenId].balance || 0) - tokenData.balance),
        };
      }
    });

    // Calculate after-reversal cash and promo balances
    const sourceAfterCashBalance = sourceCurrentCash + cashBalanceToReverse;
    const destAfterCashBalance = Math.max(0, destCurrentCash - cashBalanceToReverse);
    const sourceAfterPromoBalance = sourceCurrentPromo + promoBalanceToReverse;
    const destAfterPromoBalance = Math.max(0, destCurrentPromo - promoBalanceToReverse);

    const details = {
      tokensToRemove,
      tokensToAdd,
      cashBalanceToReverse,
      promoBalanceToReverse,
      warnings,
      sourceAfterBalance,
      destAfterBalance,
      sourceAfterCashBalance,
      destAfterCashBalance,
      sourceAfterPromoBalance,
      destAfterPromoBalance,
    };

    console.log("[REVERSAL DEBUG] Final reversal details:", details);
    console.log("[REVERSAL DEBUG] Button should be enabled:", {
      hasTokens: Object.keys(tokensToAdd).length > 0,
      hasCash: cashBalanceToReverse > 0,
      hasPromo: promoBalanceToReverse > 0,
      shouldEnable: Object.keys(tokensToAdd).length > 0 || cashBalanceToReverse > 0 || promoBalanceToReverse > 0,
    });

    setReversalDetails(details);
  };

  const formatTokens = (tokens: any): string => {
    if (!tokens || typeof tokens !== "object") return "—";

    // Check if tokens has a nested tokens_balance property (from transfer history)
    const tokenData = tokens.tokens_balance || tokens;

    if (!tokenData || typeof tokenData !== "object") return "—";
    const tokenList = Object.values(tokenData);
    if (tokenList.length === 0) return "—";
    return tokenList
      .map((t: any) => `${t.redeemable_token_name}: ${t.balance}`)
      .join(", ");
  };

  const handleConfirm = async () => {
    if (!transfer || !reversalDetails) return;

    setIsLoading(true);
    try {
      await onConfirm(
        transfer.id,
        transfer.source_uid,
        transfer.destination_uid,
        reversalDetails
      );
      toast.success("Transfer reversed successfully");
      onClose();
    } catch (error) {
      console.error("Error reversing transfer:", error);
      toast.error("Failed to reverse transfer");
      setIsLoading(false);
    }
  };

  if (!transfer) return null;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[1000]"
        initialFocus={initialFocusRef}
        onClose={isLoading ? () => {} : onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 z-[1000]"
            aria-hidden="true"
            style={{ backgroundColor: "rgba(0,0,0,0.2)" }}
          />
        </Transition.Child>

        <div className="fixed inset-0 z-[1001] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative z-[1002] transform overflow-hidden rounded-lg bg-white px-6 py-8 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                <div ref={initialFocusRef} tabIndex={-1} aria-hidden="true" />

                <Dialog.Title
                  as="h3"
                  className="text-xl font-semibold leading-6 text-gray-900 mb-4"
                >
                  Reverse Transfer
                </Dialog.Title>

                {isFetchingBalances ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">Loading current balances...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-md">
                      <h4 className="font-semibold text-gray-700 mb-2">Transfer Details</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Source UID:</span>
                          <span className="font-medium">{transfer.source_uid}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Destination UID:</span>
                          <span className="font-medium">{transfer.destination_uid}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tokens Transferred:</span>
                          <span className="font-medium">
                            {formatTokens(transfer.destination_payload)}
                          </span>
                        </div>
                        {(transfer.destination_cash_balance ?? 0) > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Cash Balance Transferred:</span>
                            <span className="font-medium">
                              ${((transfer.destination_cash_balance || 0) / 100).toFixed(2)}
                            </span>
                          </div>
                        )}
                        {(transfer.destination_promo_balance ?? 0) > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Promo Balance Transferred:</span>
                            <span className="font-medium">
                              ${((transfer.destination_promo_balance || 0) / 100).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {reversalDetails && (
                      <>
                        <div className="bg-blue-50 p-4 rounded-md">
                          <h4 className="font-semibold text-blue-900 mb-2">
                            Reversal Actions
                          </h4>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-blue-700">Tokens to Remove from Destination:</span>
                              <span className="font-medium text-blue-900">
                                {formatTokens(reversalDetails.tokensToRemove)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-blue-700">Tokens to Add Back to Source:</span>
                              <span className="font-medium text-blue-900">
                                {formatTokens(reversalDetails.tokensToAdd)}
                              </span>
                            </div>
                            {reversalDetails.cashBalanceToReverse > 0 && (
                              <div className="flex justify-between">
                                <span className="text-blue-700">Cash Balance to Reverse:</span>
                                <span className="font-medium text-blue-900">
                                  ${(reversalDetails.cashBalanceToReverse / 100).toFixed(2)}
                                </span>
                              </div>
                            )}
                            {reversalDetails.promoBalanceToReverse > 0 && (
                              <div className="flex justify-between">
                                <span className="text-blue-700">Promo Balance to Reverse:</span>
                                <span className="font-medium text-blue-900">
                                  ${(reversalDetails.promoBalanceToReverse / 100).toFixed(2)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {reversalDetails.warnings.length > 0 && (
                          <div className="bg-amber-50 border border-amber-200 p-4 rounded-md">
                            <h4 className="font-semibold text-amber-900 mb-2">Warnings</h4>
                            <ul className="list-disc list-inside space-y-1 text-sm text-amber-800">
                              {reversalDetails.warnings.map((warning, idx) => (
                                <li key={idx}>{warning}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="bg-gray-50 p-4 rounded-md">
                          <h4 className="font-semibold text-gray-700 mb-3">
                            Balance Comparison
                          </h4>
                          <div className="space-y-4 text-sm">
                            <div>
                              <p className="text-gray-600 font-medium mb-2">Source Asset ({transfer.source_uid})</p>
                              <div className="ml-3 space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Current Tokens:</span>
                                  <span className="font-medium">
                                    {formatTokens(sourceCurrentBalance)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-green-600">After Reversal Tokens:</span>
                                  <span className="font-medium text-green-700">
                                    {formatTokens(reversalDetails.sourceAfterBalance)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Current Cash:</span>
                                  <span className="font-medium">
                                    ${(sourceCurrentCash / 100).toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-green-600">After Reversal Cash:</span>
                                  <span className="font-medium text-green-700">
                                    ${(reversalDetails.sourceAfterCashBalance / 100).toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Current Promo:</span>
                                  <span className="font-medium">
                                    ${(sourceCurrentPromo / 100).toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-green-600">After Reversal Promo:</span>
                                  <span className="font-medium text-green-700">
                                    ${(reversalDetails.sourceAfterPromoBalance / 100).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="border-t border-gray-200 pt-3">
                              <p className="text-gray-600 font-medium mb-2">Destination Asset ({transfer.destination_uid})</p>
                              <div className="ml-3 space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Current Tokens:</span>
                                  <span className="font-medium">
                                    {formatTokens(destCurrentBalance)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-red-600">After Reversal Tokens:</span>
                                  <span className="font-medium text-red-700">
                                    {formatTokens(reversalDetails.destAfterBalance)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Current Cash:</span>
                                  <span className="font-medium">
                                    ${(destCurrentCash / 100).toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-red-600">After Reversal Cash:</span>
                                  <span className="font-medium text-red-700">
                                    ${(reversalDetails.destAfterCashBalance / 100).toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Current Promo:</span>
                                  <span className="font-medium">
                                    ${(destCurrentPromo / 100).toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-red-600">After Reversal Promo:</span>
                                  <span className="font-medium text-red-700">
                                    ${(reversalDetails.destAfterPromoBalance / 100).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="mt-6 flex justify-end gap-2">
                      <SecondaryButton onClick={isLoading ? undefined : onClose}>
                        Cancel
                      </SecondaryButton>
                      <PrimaryButton
                        onClick={handleConfirm}
                        isLoading={isLoading}
                        isDisabled={
                          isFetchingBalances ||
                          !reversalDetails ||
                          (Object.keys(reversalDetails.tokensToAdd).length === 0 &&
                           reversalDetails.cashBalanceToReverse === 0 &&
                           reversalDetails.promoBalanceToReverse === 0)
                        }
                      >
                        Confirm Reversal
                      </PrimaryButton>
                    </div>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
